import styled from "styled-components";
import { useState, createContext, useContext } from "react";
import { Typography } from '../TypographyStyles';
import { colors } from "../../utils/colors";
import DelayIndication from "./DelayIndication";
import { getColor } from "../../utils/colorFormatter";
import { BarChart3, ArrowRight } from 'lucide-react';
import { StyledTooltip } from '../TooltipStyles';
import { getJourneyDurationInMinutes, formatDuration } from "../../utils/durationUtils";
import * as TypographyStyles from '../TypographyStyles';
import { useTranslation } from 'react-i18next';

// Context for time display preference (similar to Swift's TimeDisplayPreference)
const TimeDisplayContext = createContext<{
  showActualTime: boolean;
  toggleShowActualTime: () => void;
}>({
  showActualTime: false,
  toggleShowActualTime: () => {},
});

// Provider component to wrap the app or journey details
export const TimeDisplayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [showActualTime, setShowActualTime] = useState(false);
  
  const toggleShowActualTime = () => {
    setShowActualTime(prev => !prev);
  };
  
  return (
    <TimeDisplayContext.Provider value={{ showActualTime, toggleShowActualTime }}>
      {children}
    </TimeDisplayContext.Provider>
  );
};

const TimesContainer = styled('div')`
  display: flex;
  grid-column-gap: 8px;
  align-items: center;
`;

const ClickableTimeWrapper = styled.div<{ $isClickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 2px;
  ${props => props.$isClickable && `
    cursor: pointer;
    user-select: none;
  `}
`;

const ActualTimeBox = styled.div<{ bgColor: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0 6px;
  border-radius: 6px;
  background: ${props => props.bgColor};
  color: ${colors.white};
`;

interface DelayContainerProps {
  $hasRealInfo: boolean; 
  $containerColor: string; // Use $ prefix to prevent passing to DOM
}

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  color: inherit;
  
  svg {
    stroke: currentColor;
  }
`;

const DelayContainer = styled('div')<DelayContainerProps>`
  color: ${colors.grey600};
  display: flex;
  grid-column-gap: 2px;
  align-items: center;
`

// Utility function to extract time from an ISO string without converting it to the user's local timezone
const formatTimeWithoutTimezoneConversion = (time: string): string => {
  try {
    // If time contains 'T', it's an ISO string, otherwise it's HH:mm:ss
    const timeString = time.includes('T') ? time.split('T')[1] : time;
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  } catch {
    console.warn('Error parsing time:', time);
    return time;
  }
};

// Calculate actual time by adding delay to planned time
// This function preserves the original timezone from the ISO string by working directly with time components
const calculateActualTime = (time: string, delaySec: number): string => {
  try {
    if (time.includes('T')) {
      // ISO string - extract time components directly without timezone conversion
      const timePart = time.split('T')[1]; // Get the time part (HH:mm:ss...)
      const timeMatch = timePart.match(/^(\d{2}):(\d{2}):(\d{2})/);
      
      if (timeMatch) {
        const [, hours, minutes, seconds] = timeMatch;
        let hour = parseInt(hours, 10);
        let minute = parseInt(minutes, 10);
        let second = parseInt(seconds, 10);
        
        // Add delay in seconds
        second += delaySec;
        
        // Handle overflow/underflow
        if (second >= 60) {
          minute += Math.floor(second / 60);
          second = second % 60;
        }
        if (minute >= 60) {
          hour += Math.floor(minute / 60);
          minute = minute % 60;
        }
        if (hour >= 24) {
          hour = hour % 24;
        }
        if (second < 0) {
          minute -= 1;
          second += 60;
        }
        if (minute < 0) {
          hour -= 1;
          minute += 60;
        }
        if (hour < 0) {
          hour += 24;
        }
        
        return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      }
      
      // Fallback: extract time string and parse
      const timeString = timePart.split(/[+-Z]/)[0]; // Remove timezone info
      const [hours, minutes, seconds = '00'] = timeString.split(':');
      let hour = parseInt(hours, 10);
      let minute = parseInt(minutes, 10);
      let second = parseInt(seconds, 10);
      
      // Add delay
      second += delaySec;
      
      // Handle overflow/underflow
      if (second >= 60) {
        minute += Math.floor(second / 60);
        second = second % 60;
      }
      if (minute >= 60) {
        hour += Math.floor(minute / 60);
        minute = minute % 60;
      }
      if (hour >= 24) {
        hour = hour % 24;
      }
      if (second < 0) {
        minute -= 1;
        second += 60;
      }
      if (minute < 0) {
        hour -= 1;
        minute += 60;
      }
      if (hour < 0) {
        hour += 24;
      }
      
      return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    } else {
      // If it's just time (HH:mm:ss), add delay directly
      const [hours, minutes, seconds = '00'] = time.split(':');
      let hour = parseInt(hours, 10);
      let minute = parseInt(minutes, 10);
      let second = parseInt(seconds, 10);
      
      // Add delay in seconds
      second += delaySec;
      
      // Handle overflow/underflow
      if (second >= 60) {
        minute += Math.floor(second / 60);
        second = second % 60;
      }
      if (minute >= 60) {
        hour += Math.floor(minute / 60);
        minute = minute % 60;
      }
      if (hour >= 24) {
        hour = hour % 24;
      }
      if (second < 0) {
        minute -= 1;
        second += 60;
      }
      if (minute < 0) {
        hour -= 1;
        minute += 60;
      }
      if (hour < 0) {
        hour += 24;
      }
      
      return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    }
  } catch {
    console.warn('Error calculating actual time:', time, delaySec);
    return formatTimeWithoutTimezoneConversion(time);
  }
};

/** Start of calendar day in local time (for day comparison). */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Days from ref to display (positive = display is later). Returns 0 if same day or ref later. */
function daysAfterReference(displayDate: Date, referenceDate: Date): number {
  const refDay = startOfDay(referenceDate).getTime();
  const displayDay = startOfDay(displayDate).getTime();
  return Math.floor((displayDay - refDay) / (24 * 60 * 60 * 1000));
}

/** Day suffix when display time is on a later day than reference, e.g. "(+1d)". */
function getDaySuffix(displayDate: Date, referenceDate: Date): string {
  const days = daysAfterReference(displayDate, referenceDate);
  return days > 0 ? `(+${days}d)` : '';
}

type TimeContainerProps = {
  time: string;
  delaySec?: number;
  averageDelaySec?: number;
  weight?: 400 | 600 | 700 ; // Restrict weight to valid values
  size?: 14 | 16,
  cancelled?: boolean;
  /** If set, show (+Nd) when the display time is on a later calendar day than this (e.g. journey start). */
  referenceDate?: string | Date;
}

const CancelledTimeText = styled(Typography)`
  text-decoration: line-through;
  color: ${colors.red} !important;
`;

const DaySuffix = styled(Typography)`
  flex-shrink: 0;
`;

export const TimeContainer = ({ time, delaySec, averageDelaySec, weight = 700, size = 14, cancelled = false, referenceDate: referenceDateProp }: TimeContainerProps) => {
  const { t } = useTranslation();
  const { showActualTime, toggleShowActualTime } = useContext(TimeDisplayContext);
  const [isHovering, setIsHovering] = useState(false);
  
  let formattedTime;
  let actualTime: string | null = null;
  
  try {
    // Use the custom function to format time without timezone conversion
    formattedTime = formatTimeWithoutTimezoneConversion(time);
    
    // Calculate actual time if there's a delay
    if (delaySec !== undefined && delaySec !== null && delaySec !== 0) {
      actualTime = calculateActualTime(time, delaySec);
    }
  } catch {
    formattedTime = time; // Fallback to raw time if formatting fails
  }
  
  const isOnTime = delaySec === 0;
  const hasDelay = delaySec !== undefined && delaySec !== null && delaySec !== 0;
  const isClickable = hasDelay && !cancelled;
  
  // Determine if we should show actual time
  // If hovering, show the opposite of the current global state
  // Otherwise, use the global state
  const shouldShowActualTime = isHovering 
    ? !showActualTime  // Show opposite when hovering
    : showActualTime;  // Use global state when not hovering
  
  // Determine display time and color
  const displayTime = shouldShowActualTime && actualTime ? actualTime : formattedTime;

  // Day suffix when display time is on a later calendar day than reference (e.g. +1d)
  const referenceDate = referenceDateProp != null ? (typeof referenceDateProp === 'string' ? new Date(referenceDateProp) : referenceDateProp) : null;
  const hasFullIso = time.includes('T');
  const displayDateTime: Date | null = hasFullIso
    ? (shouldShowActualTime && delaySec != null && delaySec !== 0
        ? new Date(new Date(time).getTime() + delaySec * 1000)
        : new Date(time))
    : null;
  const showDaySuffix = referenceDate != null && displayDateTime != null && daysAfterReference(displayDateTime, referenceDate) > 0;
  const daySuffix = referenceDate != null && displayDateTime != null ? getDaySuffix(displayDateTime, referenceDate) : '';
  const delayMinutes = delaySec ? Math.round(delaySec / 60) : 0;
  const delayColor = getColor(delayMinutes, 'delayInMinutes');
  
  // Color logic: when cancelled, use red with strikethrough
  // When showing actual time with delay, use white text on colored background
  // Otherwise use the normal color logic
  const textColor = cancelled
    ? colors.red
    : (shouldShowActualTime && hasDelay 
      ? colors.white 
      : (isOnTime ? colors.green : colors.black));
  
  const handleClick = () => {
    if (isClickable) {
      setIsHovering(false); // Reset hover state on click
      toggleShowActualTime();
    }
  };
  
  const handleMouseEnter = () => {
    if (isClickable && actualTime) {
      setIsHovering(true);
    }
  };
  
  const handleMouseLeave = () => {
    setIsHovering(false);
  };
  
  // Generate unique tooltip ID for cancelled times
  const cancelledTooltipId = cancelled ? `cancelled-time-tooltip-${time}-${delaySec || 0}` : undefined;

  return (
    <TimesContainer>
      <ClickableTimeWrapper 
        $isClickable={isClickable} 
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        data-tooltip-id={cancelled ? cancelledTooltipId : undefined}
      >
        {cancelled ? (
          <CancelledTimeText style={{fontWeight: weight, fontSize: size}}>
            {displayTime}
          </CancelledTimeText>
        ) : shouldShowActualTime && hasDelay ? (
          <ActualTimeBox bgColor={delayColor}>
            <Typography color={textColor} style={{fontWeight: weight, fontSize: size}}>
              {displayTime}
            </Typography>
          </ActualTimeBox>
        ) : (
          <>
            <Typography color={textColor} style={{fontWeight: weight, fontSize: size}}>
              {displayTime}
            </Typography>
            {hasDelay && !shouldShowActualTime && <DelayIndication size={size} weight={700} delay={delaySec!} timeUnit="seconds"/>}
          </>
        )}
      </ClickableTimeWrapper>
      {showDaySuffix && daySuffix && (
        <DaySuffix color={colors.grey600} style={{ fontWeight: 400, fontSize: size }}>{daySuffix}</DaySuffix>
      )}
      {cancelled && cancelledTooltipId && (
        <StyledTooltip 
          id={cancelledTooltipId} 
          place="top" 
          className="tooltip-styled"
        >
          {t('statistics.historyLabels.cancelled')}
        </StyledTooltip>
      )}
      {averageDelaySec != null && !cancelled ? (
        <DelayContainer 
          $hasRealInfo={!!delaySec} 
          $containerColor={getColor(averageDelaySec/60, 'delayInMinutes')}
          data-tooltip-id={`average-delay-tooltip-${time}`}
        >
          <IconWrapper>
            <BarChart3 size={size} />
          </IconWrapper>
          <DelayIndication delay={averageDelaySec} size={size} weight={400} color="inherit" timeUnit="seconds"/>
          <StyledTooltip 
            id={`average-delay-tooltip-${time}`} 
            place="top" 
            className="tooltip-styled"
          >
            {t('elements.timeComponents.averageDelayTooltip')}
          </StyledTooltip>
        </DelayContainer>
      ) : null}
    </TimesContainer>
  )
}

// TimeIndication component
const TimeInfo = styled('div')`
  display: flex;
  justify-content: space-between;
`

const TimeInfoTimes = styled('div')`
  display: flex;
  grid-column-gap: 8px;
  align-items: center;
`

const ArrowIcon = styled(ArrowRight)`
  color: ${colors.black};
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`

type TimeIndicationProps = {
  legs: any;
  journey?: any; // Optional journey object to check journey-level cancellation
  /** Reference date from the journeys request (departure or arrival search param). Used for +1d suffix. */
  referenceDate?: string | Date;
}

const CancelledBadge = styled(TypographyStyles.BodyLRegular)`
  color: ${colors.white} !important;
  background-color: ${colors.red};
  padding: 2px 12px;
  border-radius: 10px;
  font-weight: 600;
`;

const LikelyMissBadge = styled(TypographyStyles.BodyLRegular)`
  color: ${colors.black} !important;
  background-color: ${colors.yellow};
  padding: 2px 12px;
  border-radius: 10px;
  font-weight: 600;
`;

export const TimeIndication = ({ legs, journey, referenceDate: referenceDateProp }: TimeIndicationProps) => {
  const { t } = useTranslation();
  const originLeg = legs[0];
  const destinationLeg = legs[legs.length-1];
  const referenceDate = referenceDateProp ?? originLeg?.plannedDeparture;
  
  const durationInMinutes = getJourneyDurationInMinutes(legs)
  const formattedDuration = formatDuration(durationInMinutes);

  // Check if the leg is cancelled - check both the cancelled property and if departure/arrival are null
  // (null departure/arrival often indicates cancellation when there's a planned time)
  // Also check journey-level remarks for "Trip is not possible"
  const hasJourneyCancellationRemark = journey?.remarks?.some((remark: any) => 
    remark?.text?.toLowerCase().includes('not possible') || 
    remark?.summary?.toLowerCase().includes('not possible')
  );
  
  const isDepartureCancelled = originLeg?.cancelled === true || 
    hasJourneyCancellationRemark ||
    (originLeg?.plannedDeparture && !originLeg?.departure && originLeg?.departureDelay === null);
  const isArrivalCancelled = destinationLeg?.cancelled === true || 
    hasJourneyCancellationRemark ||
    (destinationLeg?.plannedArrival && !destinationLeg?.arrival && destinationLeg?.arrivalDelay === null);
  
  const isJourneyCancelled = isDepartureCancelled || isArrivalCancelled;

  // Check for missed connections
  // A connection is likely missed if actual arrival > actual departure for any transfer
  const hasImpossibleTransfers = (): boolean => {
    if (!journey || legs.length <= 1) return false;
    
    for (let i = 0; i < legs.length - 1; i++) {
      const currentLeg = legs[i];
      const nextLeg = legs[i + 1];
      
      // Get actual arrival time of current leg (or planned if actual is null)
      const currentArrival = currentLeg.arrival || currentLeg.plannedArrival;
      // Get actual departure time of next leg (or planned if actual is null)
      const nextDeparture = nextLeg.departure || nextLeg.plannedDeparture;
      
      if (currentArrival && nextDeparture) {
        // If arrival is after departure, transfer is impossible
        if (new Date(currentArrival) > new Date(nextDeparture)) {
          return true;
        }
      }
    }
    
    return false;
  };

  const isLikelyMissed = hasImpossibleTransfers();
  const likelyMissTooltipId = `likely-miss-tooltip-${journey?.id || 'default'}`;

  return (
    <TimeInfo>
      <TimeInfoTimes>
        <TimeContainer size={16} time={originLeg.plannedDeparture} delaySec={originLeg.departureDelay} cancelled={isDepartureCancelled} referenceDate={referenceDate} />
        <ArrowIcon size={16} />
        <TimeContainer size={16} time={destinationLeg.plannedArrival} delaySec={destinationLeg.arrivalDelay} cancelled={isArrivalCancelled} referenceDate={referenceDate} />
      </TimeInfoTimes>
      {isJourneyCancelled ? (
        <CancelledBadge>{t('statistics.historyLabels.cancelled')}</CancelledBadge>
      ) : isLikelyMissed ? (
        <>
          <LikelyMissBadge data-tooltip-id={likelyMissTooltipId}>
            {t('statistics.historyLabels.likelyMiss')}
          </LikelyMissBadge>
          <StyledTooltip id={likelyMissTooltipId} place="top" className="tooltip-styled">
            {t('statistics.historyLabels.likelyMissTooltip')}
          </StyledTooltip>
        </>
      ) : (
        <TypographyStyles.BodyLRegular color={colors.grey600}>{formattedDuration}</TypographyStyles.BodyLRegular>
      )}
    </TimeInfo>
  )
}

